// src/pages/academic/AcademicExamsList.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus,
  ClipboardList,
  Pencil,
  Trash2,
  ChevronRight,
  Loader2,
  ArrowRight,
} from "lucide-react";
import { academicExamsAPI, getApiError } from "../../services/academicService";
import { ACADEMIC_ROOT } from "./academicConstants";
import AcademicDeleteModal from "./components/AcademicDeleteModal";

export default function AcademicExamsList() {
  const navigate = useNavigate();
  const { categoryId } = useParams();

  const [category, setCategory] = useState(null);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    fetchData();
  }, [categoryId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await academicExamsAPI.getByCategory(categoryId);
      setCategory(data.category); // { id, name }
      setExams(data.exams || []);
    } catch (err) {
      console.error(err);
      setError(getApiError(err, "تعذر تحميل الاختبارات"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      setDeleting(true);
      setDeleteError("");
      await academicExamsAPI.delete(deleteModal.id);
      setExams((prev) => prev.filter((e) => e.id !== deleteModal.id));
      setDeleteModal(null);
    } catch (err) {
      console.error(err);
      setDeleteError(getApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const createPath = `${ACADEMIC_ROOT}/categories/${categoryId}/exams/create`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(ACADEMIC_ROOT)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowRight className="w-5 h-5 text-gray-600" />
          </button>
          <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {category?.name}
            </h1>
            <p className="text-sm text-gray-500">{exams.length} Exams</p>
          </div>
        </div>
        <button
          onClick={() => navigate(createPath)}
          className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl hover:bg-indigo-700 transition-colors font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Exam
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="font-medium underline">
            Retry
          </button>
        </div>
      )}

      {exams.length === 0 && !error ? (
        <div className="text-center py-16 text-gray-400">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No exams in this category yet</p>
          <button
            onClick={() => navigate(createPath)}
            className="mt-4 inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium"
          >
            <Plus className="w-4 h-4" />
            Add an exam now
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {exams.map((exam) => (
            <div
              key={exam.id}
              className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-all hover:border-indigo-200"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      navigate(
                        `${ACADEMIC_ROOT}/categories/${categoryId}/exams/${exam.id}/edit`
                      )
                    }
                    className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                    aria-label="Edit exam"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setDeleteError("");
                      setDeleteModal(exam);
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    aria-label="Delete exam"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  {!exam.is_active && (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                      Inactive
                    </span>
                  )}
                  {exam.icon ? (
                    <img
                      src={exam.icon}
                      alt=""
                      className="w-10 h-10 rounded-xl object-cover border border-gray-100"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                      <ClipboardList className="w-5 h-5 text-indigo-500" />
                    </div>
                  )}
                </div>
              </div>

              <h3 className="font-bold text-gray-900 text-right mb-1">
                {exam.title}
              </h3>
              {exam.description && (
                <p className="text-sm text-gray-500 text-right mb-3 line-clamp-2">
                  {exam.description}
                </p>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-3">
                <button
                  onClick={() =>
                    navigate(`${ACADEMIC_ROOT}/exams/${exam.id}/skills`)
                  }
                  className="flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                >
                  <ChevronRight className="w-4 h-4" />
                  Manage Skills
                </button>
                <span className="text-xs text-gray-400">
                  {exam.skills_count || 0} Skills · {exam.total_questions || 0}{" "}
                  Questions
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteModal && (
        <AcademicDeleteModal
          title="Delete Exam"
          itemName={deleteModal.title}
          warning="Skills inside this exam may be deleted with it."
          deleting={deleting}
          error={deleteError}
          onCancel={() => setDeleteModal(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}